import { t } from 'i18next';
import { RAID_METADATA_BY_SLUG } from '../../../../../shared/enums';
import RaidRankingsLoader from '../raidRankingsLoader';
import { FatedCountdown } from './fatedCountdown';
import { InContentAd } from '../../../../../../../shared/frontend/components/ads/inContentAd';
import { RaidRankingsContentFrontpage } from './raidRankingsContentFrontpage';
import { RecentRaidRankingsContentFrontpage } from './recentRaidRankingsContentFrontpage';
import { createActionGetRaidRankings } from '../../../actions/getRaidRankings';
import apiService from '../../../services/apiService';
import _ from 'lodash';

export type TFatedRaidsPaneProps = {
  enabled: boolean;
  seasonSlug: string;
  raidSlugs: string[];
  difficulty: string;
  title: string;
  region: string;
  realm?: string;
};

export function FatedRaidsPane(props: TFatedRaidsPaneProps) {
  if (!props.enabled) {
    return null;
  }

  return (
    <>
      <h2 className="slds-m-bottom--small slds-m-top--large">
        <span className="rio-text-title--caps text-raiderio">{t(props.title)}</span>
        <span className="slds-m-left--small slds-text-title--caps text-white">
          {t('frontpage:difficulty-raid-progress', {
            difficulty: t(`common:difficulty:${props.difficulty}.name`)
          })}{' '}
          ({t(`seasons:${props.seasonSlug}.name`)})
        </span>
      </h2>

      <div className="slds-grid slds-wrap slds-grid--pull-padded">
        {props.raidSlugs
          .map((rSlug: string) => RAID_METADATA_BY_SLUG[rSlug])
          .map((r: any) => (
            <div
              key={r.slug}
              className={`slds-is-relative slds-size--1-of-${Math.max(
                2,
                props.raidSlugs.length
              )} slds-max-small-size--1-of-1 slds-p-horizontal--xx-small`}
            >
              {r.fatedInterval && (
                <FatedCountdown
                  raid={r.slug}
                  intervalStartDate={r.fatedInterval.rotationStartsAt.us}
                  intervalEndDate={r.fatedInterval.rotationEndsAt.us}
                  intervalActiveAfterRotationDate={r.fatedInterval.alwaysActiveEndsAt.us}
                  isAlwaysActiveAfterRotation={r.fatedInterval.alwaysActiveAfterRotationEnds}
                  intervalWeeks={r.fatedInterval.weeks}
                  className="slds-p-vertical--medium rio-text--shadow"
                  style={{
                    borderTopLeftRadius: '0.25rem',
                    borderTopRightRadius: '0.25rem'
                  }}
                />
              )}

              <RaidRankingsLoader
                component={RaidRankingsContentFrontpage}
                limit={5}
                title={t(`raids:${r.slug}.name`)}
                raid={r.slug}
                difficulty={props.difficulty}
                region={props.region}
                realm={props.realm || 'all'}
                instanceId={`frontpage:raid:top:${props.region}:${props.realm || 'all'}:${r.slug}`}
                ignoreErrors={true}
              />
            </div>
          ))}
      </div>

      <InContentAd at="sm" />

      <h2 className="slds-m-bottom--small slds-m-top--large">
        <span className="rio-text-title--caps text-raiderio">{t('frontpage:section-recent-guild-progress')}</span>
        <span className="slds-m-left--small slds-text-title--caps text-white">
          {t('frontpage:raid-activity')} (${t(`seasons:${props.seasonSlug}.name`)})
        </span>
      </h2>

      <div className="slds-grid slds-wrap slds-grid--pull-padded">
        {props.raidSlugs
          .map((rSlug: string) => RAID_METADATA_BY_SLUG[rSlug])
          .map((r: any) => (
            <div
              key={r.slug}
              className={`slds-is-relative slds-size--1-of-${Math.max(
                2,
                props.raidSlugs.length
              )} slds-max-small-size--1-of-1 slds-p-horizontal--xx-small`}
            >
              <RaidRankingsLoader
                component={RecentRaidRankingsContentFrontpage}
                limit={5}
                title={t(`raids:${r.slug}.name`)}
                raid={r.slug}
                difficulty={props.difficulty}
                region={props.region}
                realm={props.realm || 'all'}
                instanceId={`frontpage:raid:recent:${props.region}:${props.realm}:${r.slug}`}
                recent={true}
                ignoreErrors={true}
              />
            </div>
          ))}
      </div>
    </>
  );
}

(FatedRaidsPane as any).preloadComponentActions = async (props: TFatedRaidsPaneProps, params: any) => {
  if (!props.enabled) {
    return [];
  }

  const promises: any[] = [];

  props.raidSlugs.forEach((rSlug: string) => {
    promises.push(
      new Promise((resolve, reject) => {
        const loadParams: any = {
          raid: rSlug,
          page: 0,
          faction: '',
          difficulty: props.difficulty,
          region: props.region,
          realm: props.realm || 'all',
          limit: 5,
          recent: false
        };

        apiService.getRaidInstanceRankings(loadParams, (err, data: any) => {
          if (err) {
            reject(err);
          } else {
            resolve([
              createActionGetRaidRankings(
                `frontpage:raid:top:${props.region}:${loadParams.realm}:${rSlug}`,
                data.raidRankings
              )
            ]);
          }
        });
      })
    );

    promises.push(
      new Promise((resolve, reject) => {
        const loadParams: any = {
          raid: rSlug,
          faction: '',
          page: 0,
          difficulty: props.difficulty,
          region: props.region,
          realm: props.realm || 'all',
          limit: 5,
          recent: true
        };

        apiService.getRaidInstanceRankings(loadParams, (err, data: any) => {
          if (err) {
            reject(err);
          } else {
            resolve([
              createActionGetRaidRankings(
                `frontpage:raid:recent:${props.region}:${props.realm}:${rSlug}`,
                data.raidRankings
              )
            ]);
          }
        });
      })
    );
  });

  return _.flatten(await Promise.all(promises));
};
